/**
 * Agent Detail Modal — popup showing agent overview, analytics, cost, settings + version management.
 */
import { useState, useEffect, useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import Modal from '@/components/common/Modal'
import Badge from '@/components/common/Badge'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '@/services/api'
import MultimodalTestModal from './MultimodalTestModal'

interface AgentVersion {
  agent_name: string
  version: string
  status: string
  changelog: string
  is_latest: boolean
  created_at: string
  definition_snapshot?: Record<string, any>
}

interface AgentDocs {
  agent_name: string
  display_name: string
  description: string
  domain: string
  version: string
  agent_type: string          // "internal" | "external"
  base_class_type: string     // "graph" | "crew"
  variant: string             // "specialized" | "standardized"
  supported_modalities: string[]
  parent_blueprint?: string | null
  derived_from?: string | null
  tier: number
  system_prompt: string | null
  tools: string[]
  sub_agents: string[]
  capabilities: Record<string, any>
  config: Record<string, any>
  membership: {
    architectures: string[]
    sub_agents: string[]
    projects: Array<{ project_id: string; version: string }>
  }
  detailed_docs?: {
    summary: string
    input: { description: string; example: string }
    output: { description: string; schema: string }
    use_cases: string[]
    constraints?: string[]
    pipeline?: string[]
  } | null
}

interface AgentDetailModalProps {
  isOpen: boolean
  onClose: () => void
  agent: any
  isCentralized?: boolean
}

type Tab = 'overview' | 'analytics' | 'cost' | 'docs' | 'membership' | 'settings'

const MODEL_OPTIONS = [
  // Gemini
  'gemini-3.1-flash-lite-preview',
  'gemini-2.5-flash',
  'gemini-2.5-flash-lite',
  'gemini-2.0-flash',
  'gemini-1.5-flash',
  'gemini-1.5-pro',
  // Anthropic
  'claude-sonnet-4-20250514',
  'claude-3-5-sonnet-20241022',
  'claude-3-opus-20240229',
  'claude-3-haiku-20240307',
  // OpenAI
  'gpt-4o',
  'gpt-4o-mini',
  'gpt-4-turbo',
  'gpt-3.5-turbo',
]

const AgentDetailModal = observer(({ isOpen, onClose, agent, isCentralized = true }: AgentDetailModalProps) => {
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const [auditData, setAuditData] = useState<any>(null)
  const [versions, setVersions] = useState<AgentVersion[]>([])
  const [loadingVersions, setLoadingVersions] = useState(false)
  const [agentDocs, setAgentDocs] = useState<AgentDocs | null>(null)
  const [showMultimodalTest, setShowMultimodalTest] = useState(false)
  const navigate = useNavigate()

  // New version form state
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newModel, setNewModel] = useState('gemini-3.1-flash-lite-preview')
  const [newTemp, setNewTemp] = useState(0.7)
  const [newPrompt, setNewPrompt] = useState('')
  const [newChangelog, setNewChangelog] = useState('')
  const [creating, setCreating] = useState(false)

  const fetchVersions = useCallback(async () => {
    if (!agent?.name) return
    setLoadingVersions(true)
    try {
      const resp = await (apiClient as any).client.get(
        `/api/v1/agents/centralized/${agent.name}/versions`
      )
      setVersions(resp.data?.versions || [])
    } catch {
      setVersions([])
    } finally {
      setLoadingVersions(false)
    }
  }, [agent?.name])

  useEffect(() => {
    if (isOpen && agent) {
      // Fetch audit data
      const fetchAudit = async () => {
        try {
          const resp = await (apiClient as any).client.get('/api/v1/audit/stats')
          const stats = resp.data
          const agentName = agent.name || agent.display_name || ''
          const agentAudit = stats?.agents?.[agentName] || null
          setAuditData({
            ...agentAudit,
            totalPlatformExecutions: stats?.total_executions || 0,
          })
        } catch {
          setAuditData(null)
        }
      }
      fetchAudit()
      fetchVersions()
      // Fetch agent documentation
      const fetchDocs = async () => {
        try {
          const resp = await (apiClient as any).client.get(
            `/api/v1/agents/centralized/${agent.name}/docs`
          )
          setAgentDocs(resp.data)
        } catch {
          setAgentDocs(null)
        }
      }
      fetchDocs()
    }
  }, [isOpen, agent, fetchVersions])

  // Pre-fill form from latest version's snapshot
  useEffect(() => {
    if (versions.length > 0) {
      const latest = versions.find(v => v.is_latest) || versions[0]
      const snap = latest?.definition_snapshot || {}
      if (snap.model) setNewModel(snap.model)
      if (snap.temperature !== undefined) setNewTemp(snap.temperature)
      if (snap.system_prompt) setNewPrompt(snap.system_prompt)
    }
  }, [versions])

  if (!agent) return null

  const suggestNextVersion = (): string => {
    if (versions.length === 0) return '1.1.0'
    const latest = versions.find(v => v.is_latest) || versions[0]
    const parts = (latest?.version || '1.0.0').split('.').map(Number)
    return `${parts[0]}.${(parts[1] || 0) + 1}.0`
  }

  const handleCreateVersion = async () => {
    if (!newChangelog.trim()) return
    setCreating(true)
    try {
      const nextVersion = suggestNextVersion()
      const configOverrides: Record<string, any> = {}
      if (newModel) configOverrides.model = newModel
      if (newTemp !== 0.7) configOverrides.temperature = newTemp
      if (newPrompt.trim()) configOverrides.system_prompt = newPrompt

      await (apiClient as any).client.post(
        `/api/v1/agents/centralized/${agent.name}/versions`,
        {
          version: nextVersion,
          changelog: newChangelog,
          config_overrides: Object.keys(configOverrides).length > 0 ? configOverrides : undefined,
        }
      )
      setShowCreateForm(false)
      setNewChangelog('')
      await fetchVersions()
    } catch (err: any) {
      console.error('Failed to create version:', err)
    } finally {
      setCreating(false)
    }
  }

  const handleRollback = async (targetVersion: string) => {
    try {
      await (apiClient as any).client.post(
        `/api/v1/agents/centralized/${agent.name}/rollback`,
        null,
        { params: { target_version: targetVersion, reason: 'Manual rollback from dashboard' } }
      )
      await fetchVersions()
    } catch (err) {
      console.error('Rollback failed:', err)
    }
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'docs', label: 'Documentation' },
    { key: 'membership', label: 'Membership' },
    { key: 'analytics', label: 'Analytics' },
    { key: 'cost', label: 'Cost' },
    { key: 'settings', label: 'Settings' },
  ]

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={agent.display_name || agent.name || 'Agent'}
      description={agent.description || ''}
      size="4xl"
    >
      {/* Tabs */}
      <div className="flex gap-1 border-b border-border -mx-6 px-6 mb-4">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            data-testid={`agent-detail-tab-${tab.key}`}
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Domain</p>
              <p className="text-sm font-medium text-foreground">{agent.domain || 'general'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Version</p>
              <div className="flex items-center gap-2">
                <Badge variant="primary">v{agent.version || '1.0.0'}</Badge>
                <span className="text-[10px] text-muted-foreground">
                  ({versions.length} version{versions.length !== 1 ? 's' : ''})
                </span>
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <Badge variant={agent.enabled !== false ? 'success' : 'error'}>
                {agent.enabled !== false ? 'Active' : 'Disabled'}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Type</p>
              <p className="text-sm font-medium text-foreground">{isCentralized ? 'Internal' : 'External'}</p>
            </div>
          </div>

          {agent.capabilities && (
            <div>
              <p className="text-xs text-muted-foreground mb-2">Capabilities</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries(agent.capabilities || {}).map(([key, val]) => (
                  <Badge key={key} variant={val ? 'primary' : 'gray'}>
                    {key.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="pt-2">
            <button
              className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              onClick={() => {
                onClose()
                navigate(`/agents/${agent.name}/execute`)
              }}
            >
              Execute Agent
            </button>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-4">
          {auditData ? (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Total Executions</p>
                  <p className="text-2xl font-bold text-foreground">{auditData.executions || 0}</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Success Rate</p>
                  <p className="text-2xl font-bold text-green-600">
                    {((auditData.success_rate || 0) * 100).toFixed(0)}%
                  </p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Avg Duration</p>
                  <p className="text-2xl font-bold text-foreground">
                    {((auditData.avg_duration_ms || 0) / 1000).toFixed(1)}s
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Successful</p>
                  <p className="text-lg font-semibold text-foreground">{auditData.successful || 0}</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Failed</p>
                  <p className="text-lg font-semibold text-red-500">
                    {(auditData.executions || 0) - (auditData.successful || 0)}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              No execution data for this agent yet
            </p>
          )}
        </div>
      )}

      {/* Cost Tab */}
      {activeTab === 'cost' && (
        <div className="space-y-4">
          {auditData ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Total Tokens</p>
                <p className="text-2xl font-bold text-foreground">
                  {(auditData.total_tokens || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Total Cost</p>
                <p className="text-2xl font-bold text-foreground">
                  ${(auditData.total_cost_usd || 0).toFixed(4)}
                </p>
              </div>
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Cost per Execution</p>
                <p className="text-lg font-semibold text-foreground">
                  ${auditData.executions ? ((auditData.total_cost_usd || 0) / auditData.executions).toFixed(4) : '0.0000'}
                </p>
              </div>
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Tokens per Execution</p>
                <p className="text-lg font-semibold text-foreground">
                  {auditData.executions ? Math.round((auditData.total_tokens || 0) / auditData.executions).toLocaleString() : '0'}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              No cost data for this agent yet
            </p>
          )}
        </div>
      )}

      {/* Documentation Tab */}
      {activeTab === 'docs' && (
        <div className="space-y-4">
          {agentDocs ? (
            <>
              {/* Rich Documentation (if available) */}
              {agentDocs.detailed_docs && (
                <>
                  {/* Summary */}
                  <div className="px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                    <p className="text-sm text-foreground">{agentDocs.detailed_docs.summary}</p>
                  </div>

                  {/* Pipeline (for crews) */}
                  {agentDocs.detailed_docs.pipeline && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Pipeline</p>
                      <div className="flex items-center gap-1 flex-wrap">
                        {agentDocs.detailed_docs.pipeline.map((stage, i) => (
                          <span key={i} className="flex items-center gap-1">
                            <span className="px-2 py-1 text-xs rounded-md bg-purple-500/10 text-purple-600 border border-purple-500/20">
                              {stage}
                            </span>
                            {i < (agentDocs.detailed_docs?.pipeline?.length || 0) - 1 && (
                              <span className="text-muted-foreground text-xs">→</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Input / Output */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Input</p>
                      <div className="px-3 py-2 bg-muted/30 border border-border rounded-lg space-y-1.5">
                        <p className="text-xs text-foreground">{agentDocs.detailed_docs.input.description}</p>
                        <pre className="text-[10px] text-muted-foreground font-mono whitespace-pre-wrap bg-muted/30 rounded px-2 py-1">
                          {agentDocs.detailed_docs.input.example}
                        </pre>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Output</p>
                      <div className="px-3 py-2 bg-muted/30 border border-border rounded-lg space-y-1.5">
                        <p className="text-xs text-foreground">{agentDocs.detailed_docs.output.description}</p>
                        <pre className="text-[10px] text-muted-foreground font-mono whitespace-pre-wrap bg-muted/30 rounded px-2 py-1 max-h-24 overflow-y-auto">
                          {agentDocs.detailed_docs.output.schema}
                        </pre>
                      </div>
                    </div>
                  </div>

                  {/* Use Cases */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Use Cases</p>
                    <ul className="space-y-1">
                      {agentDocs.detailed_docs.use_cases.map((uc, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-foreground">
                          <span className="text-emerald-500 mt-0.5">•</span>
                          {uc}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Constraints */}
                  {agentDocs.detailed_docs.constraints && agentDocs.detailed_docs.constraints.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Constraints & Safety</p>
                      <ul className="space-y-1">
                        {agentDocs.detailed_docs.constraints.map((c, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-foreground">
                            <span className="text-red-500 mt-0.5">!</span>
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}

              {/* System Prompt */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">System Prompt</p>
                {agentDocs.system_prompt ? (
                  <pre className="px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground text-xs font-mono whitespace-pre-wrap max-h-40 overflow-y-auto">
                    {agentDocs.system_prompt}
                  </pre>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No custom prompt — uses base agent behavior</p>
                )}
              </div>

              {/* Tools & Services */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Tools & Services</p>
                <div className="flex flex-wrap gap-1.5">
                  {agentDocs.tools.map((tool) => (
                    <span key={tool} className="px-2 py-1 text-xs rounded-md bg-blue-500/10 text-blue-600 border border-blue-500/20">
                      {tool}
                    </span>
                  ))}
                </div>
              </div>

              {/* Sub-Agents */}
              {agentDocs.sub_agents.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Sub-Agents Invoked</p>
                  <div className="flex flex-wrap gap-1.5">
                    {agentDocs.sub_agents.map((sa) => (
                      <span key={sa} className="px-2 py-1 text-xs rounded-md bg-purple-500/10 text-purple-600 border border-purple-500/20">
                        {sa}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Capabilities */}
              {agentDocs.capabilities && Object.keys(agentDocs.capabilities).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Capabilities</p>
                  <div className="grid grid-cols-2 gap-3">
                    {agentDocs.capabilities.operations?.length > 0 && (
                      <div>
                        <p className="text-[10px] text-muted-foreground mb-1">Operations</p>
                        <div className="flex flex-wrap gap-1">
                          {agentDocs.capabilities.operations.map((op: string) => (
                            <Badge key={op} variant="gray">{op}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {agentDocs.capabilities.input_types?.length > 0 && (
                      <div>
                        <p className="text-[10px] text-muted-foreground mb-1">Input Types</p>
                        <div className="flex flex-wrap gap-1">
                          {agentDocs.capabilities.input_types.map((t: string) => (
                            <Badge key={t} variant="gray">{t}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {agentDocs.capabilities.output_types?.length > 0 && (
                      <div>
                        <p className="text-[10px] text-muted-foreground mb-1">Output Types</p>
                        <div className="flex flex-wrap gap-1">
                          {agentDocs.capabilities.output_types.map((t: string) => (
                            <Badge key={t} variant="gray">{t}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {agentDocs.capabilities.integrations?.length > 0 && (
                      <div>
                        <p className="text-[10px] text-muted-foreground mb-1">Integrations</p>
                        <div className="flex flex-wrap gap-1">
                          {agentDocs.capabilities.integrations.map((i: string) => (
                            <Badge key={i} variant="gray">{i}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Configuration */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Configuration</p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="px-2 py-1.5 bg-muted/30 rounded border border-border">
                    <p className="text-[10px] text-muted-foreground">Quality Threshold</p>
                    <p className="text-sm font-medium text-foreground">{agentDocs.config.quality_threshold}/10</p>
                  </div>
                  <div className="px-2 py-1.5 bg-muted/30 rounded border border-border">
                    <p className="text-[10px] text-muted-foreground">Max Revisions</p>
                    <p className="text-sm font-medium text-foreground">{agentDocs.config.max_revisions}</p>
                  </div>
                  <div className="px-2 py-1.5 bg-muted/30 rounded border border-border">
                    <p className="text-[10px] text-muted-foreground">Timeout</p>
                    <p className="text-sm font-medium text-foreground">{agentDocs.config.execution_timeout}s</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {agentDocs.config.memory_layers?.length > 0 && (
                    <span className="text-[10px] text-muted-foreground">
                      Memory: {agentDocs.config.memory_layers.join(', ')}
                    </span>
                  )}
                  <span className="text-[10px] text-muted-foreground">
                    Quality review: {agentDocs.config.enable_quality_review ? 'on' : 'off'}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    Memory persistence: {agentDocs.config.enable_memory_persistence ? 'on' : 'off'}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    Audit trail: {agentDocs.config.enable_audit_trail ? 'on' : 'off'}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">Loading documentation...</p>
          )}
        </div>
      )}

      {/* Membership Tab */}
      {activeTab === 'membership' && (
        <div className="space-y-4">
          {agentDocs ? (
            <>
              {/* Architectures */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Architectures</p>
                {agentDocs.membership.architectures.length > 0 ? (
                  <div className="space-y-1.5">
                    {agentDocs.membership.architectures.map((arch) => (
                      <div key={arch} className="flex items-center gap-2 px-3 py-2 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                        <span className="text-xs font-medium text-emerald-600">{arch}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">Not part of any named architecture</p>
                )}
              </div>

              {/* Sub-Agents (if this is an orchestrator) */}
              {agentDocs.membership.sub_agents.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Orchestrates</p>
                  <div className="space-y-1">
                    {agentDocs.membership.sub_agents.map((sa) => (
                      <div key={sa} className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/5 border border-purple-500/20 rounded">
                        <span className="text-xs text-foreground">{sa}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Projects */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Project Pins</p>
                {agentDocs.membership.projects.length > 0 ? (
                  <div className="space-y-1">
                    {agentDocs.membership.projects.map((p) => (
                      <div key={p.project_id} className="flex items-center justify-between px-3 py-1.5 bg-muted/20 border border-border rounded">
                        <span className="text-xs text-foreground font-mono">{p.project_id}</span>
                        <Badge variant="gray">v{p.version}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No projects have pinned this agent</p>
                )}
              </div>

              {/* Agent Type */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Agent Info</p>
                <div className="grid grid-cols-4 gap-2">
                  <div className="px-2 py-1.5 bg-muted/30 rounded border border-border">
                    <p className="text-[10px] text-muted-foreground">Base Class</p>
                    <p className="text-sm font-medium text-foreground capitalize">{agentDocs.base_class_type || 'graph'}</p>
                  </div>
                  <div className="px-2 py-1.5 bg-muted/30 rounded border border-border">
                    <p className="text-[10px] text-muted-foreground">Domain</p>
                    <p className="text-sm font-medium text-foreground">{agentDocs.domain}</p>
                  </div>
                  <div className="px-2 py-1.5 bg-muted/30 rounded border border-border">
                    <p className="text-[10px] text-muted-foreground">Version</p>
                    <p className="text-sm font-medium text-foreground">v{agentDocs.version}</p>
                  </div>
                  <div className={`px-2 py-1.5 rounded border ${
                    agentDocs.tier === 3 ? 'bg-purple-500/10 border-purple-500/20' :
                    agentDocs.tier === 2 ? 'bg-blue-500/10 border-blue-500/20' :
                    'bg-muted/30 border-border'
                  }`}>
                    <p className="text-[10px] text-muted-foreground">Tier</p>
                    <p className={`text-sm font-medium ${
                      agentDocs.tier === 3 ? 'text-purple-600' :
                      agentDocs.tier === 2 ? 'text-blue-600' :
                      'text-foreground'
                    }`}>
                      {agentDocs.tier === 3 ? 'Multi-Action' : agentDocs.tier === 2 ? 'Domain-Aware' : 'Specialist'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Taxonomy */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Taxonomy</p>
                <div className="grid grid-cols-3 gap-2">
                  <div className={`px-2 py-1.5 rounded border ${
                    agentDocs.agent_type === 'external'
                      ? 'bg-orange-500/10 border-orange-500/20'
                      : 'bg-blue-500/10 border-blue-500/20'
                  }`}>
                    <p className="text-[10px] text-muted-foreground">Type</p>
                    <p className={`text-sm font-medium capitalize ${
                      agentDocs.agent_type === 'external' ? 'text-orange-600' : 'text-blue-600'
                    }`}>
                      {agentDocs.agent_type || 'internal'}
                    </p>
                  </div>
                  <div className={`px-2 py-1.5 rounded border ${
                    agentDocs.variant === 'standardized'
                      ? 'bg-emerald-500/10 border-emerald-500/20'
                      : 'bg-muted/30 border-border'
                  }`}>
                    <p className="text-[10px] text-muted-foreground">Variant</p>
                    <p className={`text-sm font-medium capitalize ${
                      agentDocs.variant === 'standardized' ? 'text-emerald-600' : 'text-foreground'
                    }`}>
                      {agentDocs.variant || 'specialized'}
                    </p>
                  </div>
                  <div className="px-2 py-1.5 bg-muted/30 rounded border border-border">
                    <p className="text-[10px] text-muted-foreground">Modalities</p>
                    <div className="flex gap-1 mt-0.5 flex-wrap">
                      {(agentDocs.supported_modalities || ['text']).map((m) => (
                        <span key={m} className="text-[10px] font-medium text-foreground capitalize">
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Phase 5: Test multi-modal button when agent has non-text modalities */}
                {(agentDocs.supported_modalities || []).some((m) => m !== 'text') && (
                  <div className="mt-2">
                    <button
                      onClick={() => setShowMultimodalTest(true)}
                      className="px-3 py-1.5 rounded bg-blue-500/15 text-blue-600 border border-blue-500/30 hover:bg-blue-500/25 text-xs font-medium"
                    >
                      Test multi-modal invocation →
                    </button>
                  </div>
                )}
                {(agentDocs.parent_blueprint || agentDocs.derived_from) && (
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    {agentDocs.parent_blueprint && (
                      <p>Built from blueprint: <span className="font-mono text-foreground">{agentDocs.parent_blueprint}</span></p>
                    )}
                    {agentDocs.derived_from && (
                      <p>Derived from: <span className="font-mono text-foreground">{agentDocs.derived_from}</span></p>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">Loading membership data...</p>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-4">
          {/* Current Configuration (read-only) */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Current Configuration</p>
                {(() => {
                  const latest = versions.find(v => v.is_latest) || versions[0]
                  return latest ? (
                    <Badge variant="primary">v{latest.version}</Badge>
                  ) : (
                    <Badge variant="gray">v{agent.version || '1.0.0'}</Badge>
                  )
                })()}
              </div>
              {!showCreateForm && (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  + New Version
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">LLM Model</label>
                <div className="mt-1 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground text-sm">
                  {(() => {
                    const latest = versions.find(v => v.is_latest) || versions[0]
                    return latest?.definition_snapshot?.model || 'gemini-3.1-flash-lite-preview'
                  })()}
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Temperature</label>
                <div className="mt-1 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground text-sm flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-muted/50 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full"
                      style={{ width: `${((() => {
                        const latest = versions.find(v => v.is_latest) || versions[0]
                        return latest?.definition_snapshot?.temperature ?? 0.7
                      })()) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium w-6 text-right">
                    {(() => {
                      const latest = versions.find(v => v.is_latest) || versions[0]
                      return (latest?.definition_snapshot?.temperature ?? 0.7).toFixed(1)
                    })()}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground">System Prompt</label>
              <div className="mt-1 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground text-sm max-h-16 overflow-y-auto">
                {(() => {
                  const latest = versions.find(v => v.is_latest) || versions[0]
                  return latest?.definition_snapshot?.system_prompt || (
                    <span className="text-muted-foreground italic">Using agent default prompt</span>
                  )
                })()}
              </div>
            </div>
          </div>

          {/* Create New Version (expandable) */}
          {showCreateForm && (
            <div className="border border-emerald-500/30 rounded-lg p-4 space-y-3 bg-emerald-500/5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-foreground uppercase tracking-wide">
                  New Version: v{suggestNextVersion()}
                </p>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">LLM Model</label>
                  <select
                    value={newModel}
                    onChange={(e) => setNewModel(e.target.value)}
                    className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm"
                  >
                    {MODEL_OPTIONS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">
                    Temperature: {newTemp.toFixed(1)}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={newTemp}
                    onChange={(e) => setNewTemp(parseFloat(e.target.value))}
                    className="w-full mt-2.5"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">System Prompt</label>
                <textarea
                  value={newPrompt}
                  onChange={(e) => setNewPrompt(e.target.value)}
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm h-16 resize-none"
                  placeholder="Leave empty for default..."
                />
              </div>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">
                    Changelog <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newChangelog}
                    onChange={(e) => setNewChangelog(e.target.value)}
                    className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm"
                    placeholder="What changed..."
                  />
                </div>
                <button
                  onClick={handleCreateVersion}
                  disabled={creating || !newChangelog.trim()}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:pointer-events-none shrink-0"
                >
                  {creating ? 'Creating...' : `Create v${suggestNextVersion()}`}
                </button>
              </div>
            </div>
          )}

          {/* Version History */}
          <div className="border-t border-border pt-3">
            <p className="text-xs text-muted-foreground mb-2 font-medium uppercase tracking-wide">
              Version History ({versions.length})
            </p>
            {loadingVersions ? (
              <p className="text-xs text-muted-foreground py-2">Loading...</p>
            ) : versions.length > 0 ? (
              <div className="space-y-1.5 max-h-36 overflow-y-auto">
                {versions.map((v) => (
                  <div
                    key={v.version}
                    className={`flex items-center justify-between p-2 rounded border ${
                      v.is_latest
                        ? 'bg-emerald-500/5 border-emerald-500/20'
                        : v.status === 'deprecated'
                        ? 'bg-yellow-500/5 border-yellow-500/20'
                        : 'bg-muted/20 border-border'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge variant={v.is_latest ? 'primary' : v.status === 'deprecated' ? 'warning' : 'gray'}>
                        v{v.version}
                      </Badge>
                      <span className="text-xs text-foreground truncate">{v.changelog}</span>
                      {v.definition_snapshot?.model && (
                        <span className="text-[10px] text-muted-foreground hidden sm:inline">
                          ({v.definition_snapshot.model})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {v.is_latest ? (
                        <span className="text-xs text-emerald-600 font-medium">Latest</span>
                      ) : v.status === 'active' ? (
                        <button
                          onClick={() => handleRollback(v.version)}
                          className="text-xs text-blue-500 hover:text-blue-600 font-medium"
                        >
                          Rollback
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">{v.status}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground py-2">No versions recorded yet</p>
            )}
          </div>
        </div>
      )}

      {/* Phase 5: Multi-modal test modal — only rendered when an agent with non-text
          modalities exists. Nested inside AgentDetailModal so it shares the same z-stack. */}
      {agentDocs && (
        <MultimodalTestModal
          isOpen={showMultimodalTest}
          onClose={() => setShowMultimodalTest(false)}
          agentName={agentDocs.agent_name}
          displayName={agentDocs.display_name}
          supportedModalities={agentDocs.supported_modalities || ['text']}
        />
      )}
    </Modal>
  )
})

export default AgentDetailModal
